import {
  UseMutationOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import api from "../config";
import { Objective, ObjectiveSet } from "./types";

export const useCreateObjective = (
  props?: UseMutationOptions<
    any,
    unknown,
    { accountId: string; data: any },
    unknown
  >
) =>
  useMutation<Objective, unknown, { accountId: string; data: Objective }>({
    mutationKey: ["createObjective"],
    mutationFn: async ({ accountId, data }) => {
      const response = await api.post(`/${accountId}/objective`, data);
      return response.data;
    },
    ...props,
  });

export const useFetchObjectives = (
  accountId: string,
  ownerType: string,
  orderBy: string,
  timePeriodIds: string[],
  profileOwnersToFilter: string[] = [],
  teamOwnersToFilter: string[] = [],
  departmentOwnersToFilter: string[] = []
) => {
  return useQuery<Objective[]>({
    queryKey: [
      "fetchObjectives",
      accountId,
      ownerType,
      orderBy,
      timePeriodIds,
      profileOwnersToFilter,
      teamOwnersToFilter,
      departmentOwnersToFilter,
    ],
    queryFn: async () => {
      if (timePeriodIds === null || timePeriodIds === undefined) {
        return null;
      }
      const timePeriodQuery = timePeriodIds
        .map((timePeriodId) => `&timePeriodId=${timePeriodId}`)
        .join("&");
      const orderByQuery = orderBy ? `&orderBy=${orderBy}` : "";
      const profileOwnerQuery = profileOwnersToFilter
        .map((ownerId) => `&ownerId=${ownerId}`)
        .join("&");
      const teamOwnerQuery = teamOwnersToFilter
        .map((teamId) => `&teamId=${teamId}`)
        .join("&");
      const departmentOwnerQuery = departmentOwnersToFilter
        .map((departmentId) => `&departmentId=${departmentId}`)
        .join("&");
      const response = await api.get(
        `/${accountId}/objective?ownerType=${ownerType}${orderByQuery}${timePeriodQuery}${profileOwnerQuery}${teamOwnerQuery}${departmentOwnerQuery}`
      );
      return response.data;
    },
  });
};

export const useFetchObjectiveTree = (
  accountId: string,
  objectiveId: string
) => {
  return useQuery<any>({
    queryKey: ["fetchObjectivetree", accountId, objectiveId],
    queryFn: async () => {
      const response = await api.get(
        `/${accountId}/objective/tree?objectiveId=${objectiveId}`
      );
      return response.data;
    },
  });
};

export const useUpdateObjective = (
  props?: UseMutationOptions<
    any,
    unknown,
    { accountId: string; data: any },
    unknown
  >
) =>
  useMutation<
    Objective,
    unknown,
    { accountId: string; objectiveId: string; data: Objective }
  >({
    mutationKey: ["updateObjective"],
    mutationFn: async ({ accountId, objectiveId, data }) => {
      const response = await api.put(
        `/${accountId}/objective/${objectiveId}`,
        data
      );
      return response.data;
    },
    ...props,
  });

export const useUpdateObjectiveSet = (
  props?: UseMutationOptions<
    any,
    unknown,
    { accountId: string; data: any },
    unknown
  >
) =>
  useMutation<
    ObjectiveSet,
    unknown,
    { accountId: string; objectiveSetId: string; data: ObjectiveSet }
  >({
    mutationKey: ["updateObjectiveSet"],
    mutationFn: async ({ accountId, objectiveSetId, data }) => {
      const response = await api.put(
        `/${accountId}/objectiveSet/${objectiveSetId}`,
        data
      );
      return response.data;
    },
    ...props,
  });

export const useFetchObjective = (accountId: string, objectiveId: string) => {
  return useQuery<Objective>({
    queryKey: ["fetchObjective", objectiveId],
    queryFn: async () => {
      const response = await api.get(`/${accountId}/objective/${objectiveId}`);
      return response.data;
    },
  });
};

export const useDeleteObjective = (
  props?: UseMutationOptions<
    any,
    Error,
    {
      accountId?: string;
      keyResultId: string;
      objectiveId: string;
    }
  >
) =>
  useMutation({
    mutationKey: ["deleteObjective"],
    mutationFn: async ({ accountId, objectiveId }) => {
      const response = await api.delete(
        `/${accountId}/objective/${objectiveId}`
      );
      return response.data;
    },

    ...props,
  });
